import React from 'react';
import {
  Box,
  Button,
  Collapse,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import {
  INCIDENT_OWNERSHIP_QUEUE_OPTIONS,
  ownershipQueueRequiresRawTable,
  type IncidentOpsQueueFilter,
} from '../../domain/incidents/incidentOwnershipQueueFilter';

export interface IncidentFiltersBarProps {
  ctx: Record<string, any>;
}

const IncidentFiltersBar: React.FC<IncidentFiltersBarProps> = ({ ctx }) => {
  const {
    tabValue, setTabValue,
    tableMode, setTableMode,
    sortBySeverity, setSortBySeverity,
    setSortModel,
    _defaultSortModel,
    operatorPreset, setOperatorPreset, _applyPreset,
    timePreset, setTimePreset,
    customFrom, setCustomFrom,
    customTo, setCustomTo,
    filterMinAgeMinutes, setFilterMinAgeMinutes,
    timeRange,
    searchIncidentId, setSearchIncidentId,
    tableFiltersOpen, setTableFiltersOpen,
    advancedTableFilterCount,
    filterIncidentType, setFilterIncidentType,
    filterZoneId, setFilterZoneId,
    filterCameraId, setFilterCameraId,
    filterDeviceId, setFilterDeviceId,
    filterSeverity, setFilterSeverity,
    filterIncidentStatus, setFilterIncidentStatus,
    filterWorkflowStatus, setFilterWorkflowStatus,
    filterOpsAssignee, setFilterOpsAssignee,
    filterOpsQueue, setFilterOpsQueue,
    incidentTypeOptions, zoneOptions, cameraOptions, deviceOptions,
    zoneNameById, cameraNameById, deviceNameById,
    _renderAllLabel, _incidentTypeLabel: incidentTypeLabel, _titleCase, _warehouseUnitLabel, _shortId,
    searchParams, setSearchParams, fetchData,
  } = ctx;

  const handleTableModeChange = (_: React.MouseEvent<HTMLElement>, nextMode: string | null) => {
    if (!nextMode) return;
    setTableMode(nextMode);
    setSortModel(_defaultSortModel(nextMode, nextMode === 'raw' ? sortBySeverity : false));
    const next = new URLSearchParams(searchParams);
    next.delete('tableMode');
    next.set('table_mode', nextMode);
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setSearchIncidentId('');
    setFilterIncidentType('');
    setFilterSeverity('');
    setFilterZoneId('');
    setFilterDeviceId('');
    setFilterCameraId('');
    setFilterIncidentStatus('');
    setFilterWorkflowStatus('');
    setFilterOpsAssignee('');
    setFilterOpsQueue('');
    setFilterMinAgeMinutes('');
    setOperatorPreset('');
    setSortBySeverity(false);
    setSortModel(_defaultSortModel(tableMode, false));
    const next = new URLSearchParams(searchParams);
    next.delete('camera_id');
    next.delete('device_id');
    next.delete('device');
    next.delete('incident_id');
    setSearchParams(next, { replace: true });
  };

  const persistFilterUrl = () => {
    const next = new URLSearchParams(searchParams);
    if (filterCameraId.trim()) next.set('camera_id', filterCameraId.trim());
    else next.delete('camera_id');
    if (filterDeviceId.trim()) next.set('device_id', filterDeviceId.trim());
    else next.delete('device_id');
    next.delete('device');
    setSearchParams(next, { replace: true });
    fetchData();
  };

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} sx={{ minHeight: 40 }}>
          <Tab value={1} label="Active queue" sx={{ minHeight: 40 }} />
          <Tab value={2} label="Completed" sx={{ minHeight: 40 }} />
        </Tabs>

        <ToggleButtonGroup
          size="small"
          value={tableMode}
          exclusive
          onChange={handleTableModeChange}
        >
          <ToggleButton value="grouped">Patterns</ToggleButton>
          <ToggleButton value="raw">Individual observations</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: timePreset === 'custom' ? 1 : 0.75,
        }}
      >
        <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 140 }}>
          <InputLabel>Queue</InputLabel>
          <Select
            label="Queue"
            value={filterOpsQueue}
            onChange={(e) => {
              const next = ((e.target.value as IncidentOpsQueueFilter) || '') as IncidentOpsQueueFilter;
              setFilterOpsQueue(next);
              if (next && ownershipQueueRequiresRawTable(next)) setTableMode('raw');
            }}
          >
            <MenuItem value=""><em>All queues</em></MenuItem>
            {INCIDENT_OWNERSHIP_QUEUE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
            <MenuItem value="escalations">Escalations</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
            <MenuItem value="unassigned_high_risk">Unassigned High Risk</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 140 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            label="Time Range"
            value={timePreset}
            onChange={(e) => setTimePreset(e.target.value as any)}
          >
            <MenuItem value="15m">Last 15m</MenuItem>
            <MenuItem value="1h">Last 1h</MenuItem>
            <MenuItem value="24h">Last 24h</MenuItem>
            <MenuItem value="today">Today (UTC)</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: '1 1 120px', minWidth: 120 }}>
          <InputLabel>Severity</InputLabel>
          <Select
            label="Severity"
            value={filterSeverity}
            onChange={(e) => setFilterSeverity((e.target.value as any) || '')}
          >
            <MenuItem value=""><em>All severities</em></MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: '1 1 120px', minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={filterIncidentStatus}
            onChange={(e) => setFilterIncidentStatus((e.target.value as any) || '')}
          >
            <MenuItem value=""><em>{tabValue === 2 ? 'All closed states' : tabValue === 1 ? 'All open states' : 'All statuses'}</em></MenuItem>
            <MenuItem value="OPEN">Needs review</MenuItem>
            <MenuItem value="ACKED">Assigned</MenuItem>
            <MenuItem value="IN_REVIEW">In review</MenuItem>
            <MenuItem value="CLOSED">Resolved</MenuItem>
            <MenuItem value="SUPPRESSED">Suppressed</MenuItem>
          </Select>
        </FormControl>
        <Button
          size="small"
          variant={tableFiltersOpen ? 'contained' : 'outlined'}
          startIcon={<TuneIcon />}
          endIcon={<ExpandMoreIcon sx={{ transform: tableFiltersOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 120ms ease' }} />}
          onClick={() => setTableFiltersOpen(!tableFiltersOpen)}
          aria-expanded={tableFiltersOpen}
          sx={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}
        >
          Advanced Filters{advancedTableFilterCount > 0 ? ` (${advancedTableFilterCount})` : ''}
        </Button>
      </Box>

      {timePreset === 'custom' && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 0.75 }}>
          <TextField
            label="From"
            size="small"
            type="datetime-local"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 180px', minWidth: 180 }}
          />
          <TextField
            label="To"
            size="small"
            type="datetime-local"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 180px', minWidth: 180 }}
          />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Current window: {timeRange.label}
      </Typography>

      <Collapse in={tableFiltersOpen}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Queue focus</InputLabel>
                <Select
                  label="Queue focus"
                  value={operatorPreset}
                  onChange={(e) => _applyPreset((e.target.value as any) || '')}
                >
                  <MenuItem value=""><em>Custom</em></MenuItem>
                  <MenuItem value="unack_high">Needs review + high severity</MenuItem>
                  <MenuItem value="open_gt_10m">Needs attention &gt; 10 min</MenuItem>
                  <MenuItem value="repeat_zone">Repeat incidents in one zone</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Min Age (min)"
                size="small"
                type="number"
                value={filterMinAgeMinutes}
                onChange={(e) => {
                  const value = String(e.target.value || '').trim();
                  setFilterMinAgeMinutes(value ? Math.max(0, Number(value) || 0) : '');
                  if (operatorPreset) setOperatorPreset('');
                }}
                fullWidth
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Incident Type</InputLabel>
                <Select
                  label="Incident Type"
                  value={filterIncidentType}
                  displayEmpty
                  renderValue={(value) => _renderAllLabel('incident types', value, (id: string) => incidentTypeLabel(id))}
                  onChange={(e) => setFilterIncidentType(String(e.target.value || ''))}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {incidentTypeOptions.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Search Incident ID"
                size="small"
                value={searchIncidentId}
                onChange={(e) => setSearchIncidentId(e.target.value)}
                disabled={tableMode === 'grouped'}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Zone</InputLabel>
                <Select
                  label="Zone"
                  value={filterZoneId}
                  displayEmpty
                  renderValue={(value) => _renderAllLabel('zones', value, (id: string) => zoneNameById.get(id) || id)}
                  onChange={(e) => setFilterZoneId(String(e.target.value || ''))}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {zoneOptions.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                  {!!filterZoneId && !zoneNameById.has(filterZoneId) && <MenuItem value={filterZoneId}>{filterZoneId}</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Camera</InputLabel>
                <Select
                  label="Camera"
                  value={filterCameraId}
                  displayEmpty
                  renderValue={(value) => _renderAllLabel('cameras', value, (id: string) => cameraNameById.get(id) || id)}
                  onChange={(e) => setFilterCameraId(String(e.target.value || ''))}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {cameraOptions.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                  {!!filterCameraId && !cameraNameById.has(filterCameraId) && <MenuItem value={filterCameraId}>{filterCameraId}</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Device</InputLabel>
                <Select
                  label="Device"
                  value={filterDeviceId}
                  displayEmpty
                  renderValue={(value) => _renderAllLabel('devices', value, (id: string) => deviceNameById.get(id) || _warehouseUnitLabel(id) || id)}
                  onChange={(e) => setFilterDeviceId(String(e.target.value || ''))}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {deviceOptions.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                  {!!filterDeviceId && !deviceNameById.has(filterDeviceId) && (
                    <MenuItem value={filterDeviceId}>{_shortId(filterDeviceId) || filterDeviceId}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Action status</InputLabel>
                <Select
                  label="Action status"
                  value={filterWorkflowStatus}
                  displayEmpty
                  renderValue={(value) => _renderAllLabel('action states', value, (id: string) => _titleCase(id))}
                  onChange={(e) => setFilterWorkflowStatus((e.target.value as any) || '')}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="Needs review">Needs review</MenuItem>
                  <MenuItem value="Assigned">Assigned</MenuItem>
                  <MenuItem value="Needs action">Needs action</MenuItem>
                  <MenuItem value="Resolved">Resolved</MenuItem>
                  <MenuItem value="Reviewed">Reviewed</MenuItem>
                  <MenuItem value="Suppressed">Suppressed</MenuItem>
                  <MenuItem value="Unmapped">Unmapped</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Action owner"
                size="small"
                value={filterOpsAssignee}
                onChange={(e) => setFilterOpsAssignee(String(e.target.value || ''))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                <ToggleButton
                  size="small"
                  value="sevSort"
                  selected={sortBySeverity && tableMode === 'raw'}
                  disabled={tableMode !== 'raw'}
                  onChange={() => {
                    const next = !sortBySeverity;
                    setSortBySeverity(next);
                    setSortModel(_defaultSortModel('raw', next));
                    if (operatorPreset) setOperatorPreset('');
                  }}
                >
                  Sort: Severity
                </ToggleButton>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" onClick={resetFilters}>
                    Clear Filters
                  </Button>
                  <Button size="small" variant="contained" startIcon={<RefreshIcon />} onClick={persistFilterUrl}>
                    Apply
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default IncidentFiltersBar;
